import React, { useState, useEffect, useContext } from "react";

import { Logo } from "../Logo";

import { AvatarDropdown } from "../../Avatar";
import { LeftSidebarProps, OpenNavGroups } from "./types";
import { AppSectionNavGroups } from "./AppSectionNavGroups";
import { SupportSectionNavGroups } from "./SupportSectionNavGroups";
import { AppContext } from "@/AppContext";
import Button from "@/ui/Button";
import { ButtonTypes } from "@/ui/types";
import { Link, useLocation } from "react-router-dom";
import { Card } from "@/ui/Card";
import { Ad } from "./Ad";
import { PlusIcon } from "lucide-react";
import { Tooltip } from "../../Tooltip";
import { NavGroup } from "./NavGroup";
import { UserMenu } from "@/Apps/Agents/UserMenu";
import { UnifiedUserMenu } from "@/ui/Avatar/UnifiedUserMenu";
import { useUnifiedShell } from "@/access/useUnifiedShell";

export const LeftSidebar: React.FC<LeftSidebarProps> = ({
  menuItems = [],
  bottomMenuItems = [],
  showLogo = true,
  showAvatarDropdown = true,
  showAppSection = true,
  showSupportSection = true,
  avatarSettings = {},
  showCopilotButton = false,
  type = "default",
}) => {
  // Define state for the open navigation groups
  const [openNavGroups, setOpenNavGroups] = useState<OpenNavGroups>({
    dashboard: true,
    apps: false,
    components: false,
    elements: false,
    charts: false,
    widgets: false,
    fonticons: false,
    drag: false,
    table: false,
    forms: false,
    users: false,
    pages: false,
    authentication: false,
    docs: false,
    funnels: false,
    kitchensink: false,
    ui: false,
  });

  // Define state for the avatar dropdown
  const [open, setOpen] = useState(false);

  const { sidebarOpen, setSidebarOpen, permissions } = useContext(AppContext);
  // Rollout switch: when on, one menu replaces both; when off, the old menus render exactly as before.
  const unifiedShell = useUnifiedShell();
  // Subscribe to location so pinned bottom items re-highlight on client-side nav.
  const { pathname } = useLocation();

  // Function to toggle the open navigation groups
  const doToggle: (str: any) => void = (str) => {
    setOpenNavGroups((prevOpenNavGroups) => {
      const newOpenNavGroups = { ...prevOpenNavGroups };
      Object.keys(newOpenNavGroups).forEach((key) => {
        newOpenNavGroups[key] = key === str ? !prevOpenNavGroups[str] : false;
      });
      return newOpenNavGroups;
    });
  };

  // Function to load in all menu items
  const loadInAllMenuItems = async () => {
    // Add to possible toggle states
    menuItems.forEach(({ label }) => {
      setOpenNavGroups((prevOpenNavGroups) => {
        return {
          ...prevOpenNavGroups,
          [label]: false,
        };
      });
    });
  };

  // Toggle the left sidebar
  const toggleLeftSidebar = () => {
    localStorage.setItem("sidebarOpen", !sidebarOpen);
    setSidebarOpen(!sidebarOpen);
  };

  // Load in all menu items on component mount
  useEffect(() => {
    loadInAllMenuItems();
  }, []);

  // Render the component
  return (
    <>
      <nav
        data-testid="LeftSidebar"
        className={`sidebar fixed top-0 bottom-0 z-40 flex-none border-r border-black/10 dark:border-white/10 transition-all duration-300 bg-white dark:bg-black/30 backdrop-blur text-sm ${type === "minimal" ? "w-[60px]" : "w-[212px]"}`}
      >
        {/* {JSON.stringify({sidebarOpen})} */}
        <div className={`h-full ${type === "minimal" ? "px-0" : ""}`}>
          {/* Avatar Dropdown — hidden for agent-tier users (the platform avatar
              menu links to platform-only screens like /profile). Agents get their
              client switcher below + Sign Out lives in Agents → Settings. */}
          {unifiedShell && showAvatarDropdown && <UnifiedUserMenu />}
          {!unifiedShell && showAvatarDropdown && !permissions?.includes("agents") && (
            <div className={`flex items-center justify-between py-5 ${type === "minimal" ? "px-2" : "px-7"}`}>
              <AvatarDropdown
                open={open}
                setOpen={setOpen}
                settings={avatarSettings}
                showName={type === "default"}
              />
              {/* <button
                type="button"
                className="text-black dark:text-white"
                onClick={toggleLeftSidebar}
                data-testid="Topbar-LeftSidebarToggle"
              >
                <SidebarToggleIcon />
              </button> */}
            </div>
          )}

          {/* Agents top-left avatar = the signed-in USER menu (AMB-359/A3). Client
              selection moved to the breadcrumb switcher (AppShellV8), so the rail's old
              ClientSwitcher chip is replaced by the user menu here. */}
          {!unifiedShell && permissions?.includes("agents") && <UserMenu />}

          {showCopilotButton && permissions.includes("copilot") && (
            <div className="flex justify-center py-3">
              <Tooltip 
                content="Start a new chat" 
                disabled={type === "default"}
                position="right"
              >
                <a href="/copilot">
                  <Button
                    label={type === "default" ? "+ Start a New Chat" : <PlusIcon className="w-4 h-4" />}
                    type={type === "default" ? ButtonTypes.OUTLINED : ButtonTypes.SOFT}
                    className="w-full"
                  />
                </a>
              </Tooltip>
            </div>
          )}

          {/* Menu Items */}
          {/* <ul className="relative h-[calc(100vh-475px)] flex flex-col gap-2 overflow-y-auto overflow-x-hidden py-0"> */}
          <ul className={`relative flex flex-col gap-2 overflow-y-auto overflow-x-visible py-0 ${type === "minimal" ? "mx-2" : ""}`}>
            {/* App Section */}
            {showAppSection && (
              <AppSectionNavGroups
                doToggle={doToggle}
                openNavGroups={openNavGroups}
                menuItems={menuItems}
                type={type}
              />
            )}
          </ul>

          <div className="mt-auto absolute bottom-1 left-0 right-0">
            {/* Pinned app-drawer items (e.g. Agents: KB / Activity / Settings),
                contributed via the APP_DRAWER "bottom" slot, permission-gated by
                the contributing app. */}
            {bottomMenuItems.length > 0 && (
              <ul className={`mb-1 ${type === "minimal" ? "mx-2" : ""}`}>
                {bottomMenuItems.map(({ href = "", icon, label }, key) => (
                  <NavGroup
                    key={key}
                    name={label}
                    href={href}
                    icon={icon}
                    isOpen={pathname === href || pathname.startsWith(href + "/")}
                    type={type}
                  />
                ))}
              </ul>
            )}
            {/* Support Section */}
            <ul className={`${type === "minimal" ? "mx-2" : ""}`}>
              {showSupportSection && (
                <SupportSectionNavGroups
                  doToggle={doToggle}
                  openNavGroups={openNavGroups}
                  type={type}
                />
              )}
            </ul>
            {/* Show Ad */}
            {/* <Ad /> */}
          </div>

          {/* App Logo */}
          {showLogo && (
            <div style={{ marginTop: -50 }}>
              <Logo className="p-8 py-0" />
            </div>
          )}
        </div>
      </nav>
    </>
  );
};
